import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Modal } from '@kep-ui-kit/ui-components';
import { FormikProps } from 'formik';
import { SelectOption } from '@kep-ui-kit/ui-components';

import {
  StyledModalContainer,
  StyledModalTitle,
  StyledButton,
} from '@selfserve/react-ui';
import { buttonIds } from '@selfserve/react-utils';

import { FormValues } from '../feature/plans-overview';

import { AddOnItem, PlanItemInfo } from '../data';
import { UnsubscribeFields } from './unsubscribe-fields';
import { UpdateFields } from './update-fields';

const StyledPlanModal = styled(Modal)(({ theme }: { theme?: any }) => ({
  '.MuiPaper-root': {
    maxWidth: '584px',
  },
}));

const StyledPlanModalContainer = styled(StyledModalContainer)`
  padding-bottom: 0;
`;

const StyledPlanModalTitle = styled(StyledModalTitle)(
  ({ theme }: { theme?: any }) => ({
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      marginTop: '20px',
    },
  })
);

const StyledButtonsContainer = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  marginTop: 'auto',
  padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
  borderTop: `1px solid ${theme.palette.grey['400']}`,
  justifyContent: 'flex-end',
  '> :first-of-type': {
    marginRight: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledForm = styled('form')`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

interface Props {
  open: boolean;
  isUnsubscribe: boolean;
  isAddOn: boolean;
  planOptions: Array<SelectOption>;
  addonOptions: Array<SelectOption>;
  selectedItem?: PlanItemInfo | AddOnItem;
  handleClose: () => void;
  setId: (id: string) => void;
  formik: FormikProps<FormValues>;
}

export const PlanManagingModal = ({
  open,
  isUnsubscribe,
  isAddOn,
  selectedItem,
  planOptions,
  addonOptions,
  handleClose,
  setId,
  formik,
}: Props) => {
  const { t } = useTranslation();
  const { handleSubmit, values } = formik;
  return (
    <StyledPlanModal
      backdropColor="rgba(0,110,250,.8)"
      onClose={handleClose}
      fullWidth
      open={open}
    >
      <StyledForm onSubmit={handleSubmit}>
        <StyledPlanModalContainer>
          <StyledPlanModalTitle className="dialog-title">
            {isUnsubscribe
              ? t('common.unsubscribe-request')
              : t(
                  `common.${
                    isAddOn ? 'update-addon-request' : 'update-plan-request'
                  }`
                )}
          </StyledPlanModalTitle>
          {isUnsubscribe ? (
            <UnsubscribeFields selectedItem={selectedItem} formik={formik} />
          ) : (
            <UpdateFields
              isAddOn={isAddOn}
              addonOptions={addonOptions}
              planOptions={planOptions}
              selectedItem={selectedItem}
              setId={setId}
              formik={formik}
            />
          )}
        </StyledPlanModalContainer>
        <StyledButtonsContainer>
          {isUnsubscribe ? (
            <>
              <StyledButton
                withoutMargin
                isAddOn
                isBorderless
                onClick={handleClose}
                id={
                  isAddOn
                    ? buttonIds.addonUnsubscribeKeep
                    : buttonIds.planUnsubscribeKeep
                }
              >
                {isAddOn ? t('common.keep-addon') : t('common.keep-plan')}
              </StyledButton>
              <StyledButton
                withoutMargin
                color="danger"
                type="submit"
                disabled={!values.reason}
                id={
                  isAddOn
                    ? buttonIds.addonUnsubscribeRequest
                    : buttonIds.planUnsubscribeRequest
                }
              >
                {t('common.unsubscribe-request')}
              </StyledButton>
            </>
          ) : (
            <>
              <StyledButton
                withoutMargin
                isAddOn
                onClick={handleClose}
                isBorderless
                id={
                  isAddOn
                    ? buttonIds.addonPlanUpdateCancel
                    : buttonIds.planUpdatePlanCancel
                }
              >
                {t('common.cancel')}
              </StyledButton>
              <StyledButton
                withoutMargin
                type="submit"
                disabled={!values.extendedInfo}
                id={
                  isAddOn
                    ? buttonIds.addonPlanUpdateRequest
                    : buttonIds.planUpdatePlanRequest
                }
              >
                {t(
                  `common.${
                    isAddOn ? 'update-addon-request' : 'update-plan-request'
                  }`
                )}
              </StyledButton>
            </>
          )}
        </StyledButtonsContainer>
      </StyledForm>
    </StyledPlanModal>
  );
};
